import type { ColDef, Column, GridApi, IRowNode } from "@ag-grid-community/core";
import { EMPTY_ARRAY } from "@regrello/core-utils";
import type {
  FormFieldConstraintFields,
  FormSectionBaseFields,
  LoadTableQuery,
  SpectrumFieldVersionFields,
} from "@regrello/graphql-api";
import type { FieldValues } from "react-hook-form";
import { v4 as uuid } from "uuid";

import { getFieldInstanceFormFieldIdOrThrow } from "../../../../utils/customFields/getFieldInstanceFormFieldIdOrThrow";
import { DocumentFieldPlugin } from "../../../molecules/customFields/plugins/DocumentFieldPlugin";
import { RegrelloObjectFieldPlugin } from "../../../molecules/customFields/plugins/RegrelloObjectFieldPlugin";
import { SignatureFieldPlugin } from "../../../molecules/customFields/plugins/SignatureFieldPlugin";
import { SpectrumDocumentFieldPluginDecorator } from "../../../molecules/spectrumFields/plugins/document/SpectrumDocumentFieldPluginDecorator";
import { SpectrumObjectFieldPluginDecorator } from "../../../molecules/spectrumFields/plugins/regrelloObject/SpectrumObjectFieldPluginDecorator";
import { SpectrumSignatureFieldPluginDecorator } from "../../../molecules/spectrumFields/plugins/signature/SpectrumSignatureFieldPluginDecorator";
import { SpectrumFieldPluginRegistrar } from "../../../molecules/spectrumFields/registry/spectrumFieldPluginRegistrar";
import type { FrontendSpectrumFormInstance } from "../../modals/taskDetailView/_internal/form/spectrumFormInstanceUtils";
import type { DragItemPayload } from "../spectrumForm/formBuilder/_internal/content/_internal/dndUtils";
import type { ColumnDefContext } from "../spectrumForm/formBuilder/_internal/content/table/FormBuilderTableColumnHeader";

export const GRID_OPTION_KEY_COLUMN_DEFS = "columnDefs" as const;
export type FormBuilderTableRowDatum = Record<`column-${number | string}`, string | FormFieldConstraintFields>;

const COLUMN_ID_PREFIX = "column-" as const;

export type RowDatum = {
  id: string;
  [key: `${typeof COLUMN_ID_PREFIX}${string}`]: unknown;
};

const COLUMN_ID_REGEX = new RegExp(`^${COLUMN_ID_PREFIX}\\d+$`);

/**
 * Creates a new row with a unique client-generated ID. Does not include any column data yet. (Use
 * `getColumnIdTyped` to help set column data on the row returned from this function.)
 *
 * @example
 * ```ts
 * const newRow = createRowDatum();
 * ```
 */
export function createRowDatum(): RowDatum {
  return {
    id: uuid(),
  };
}

/**
 * Returns a strongly typed column ID for the given column, such that it can be used to set or
 * access column data in a `RowDatum` without casting.
 *
 * @example
 * ```ts
 * const columnId = getColumnIdTyped(column);
 * const rowDatum: RowDatum = {
 *   id: "1",
 *   [columnId]: "value",
 * };
 * ```
 */
export function getColumnIdTyped(column: Column<unknown>): "id" | `column-${string}` {
  const columnId = column.getColId();
  if (columnId == null) {
    throw new Error("Column ID is null");
  }
  if (!COLUMN_ID_REGEX.test(columnId) && columnId !== "id") {
    throw new Error(`Column ID is invalid: ${columnId}`);
  }
  return columnId as "id" | `column-${string}`;
}

/**
 * Extracts the form field ID from a column ID.
 *
 * @example
 * ```ts
 * const formFieldId = extractFormFieldIdFromColumnId(getColumnIdTyped(column));
 * ```
 */
export function extractFormFieldIdFromColumnId(columnId: string): number {
  if (!COLUMN_ID_REGEX.test(columnId)) {
    throw new Error(`Column ID is invalid: ${columnId}`);
  }
  return Number.parseInt(columnId.slice(COLUMN_ID_PREFIX.length), 10);
}

/**
 * Focuses a column and selects all visible cells in that column.
 */
export function focusColumn(api: GridApi, columnKey: string) {
  api.setFocusedHeader(columnKey);
  api.clearCellSelection();
  api.addCellRange({
    rowStartIndex: 0,
    rowEndIndex: api.getLastDisplayedRowIndex(),
    columns: [columnKey],
  });
}

/**
 * Returns a column ID for a given index.
 */
export function getColumnIdFromIndex(index: number): `column-${string}` {
  // eslint-disable-next-line lingui/no-unlocalized-strings
  return `column-empty-${index}`;
}

/** Returns a string column ID that is unique within the current workspace. */
export function getColumnIdFromFormFieldId(formFieldId: number): `column-${string}` {
  // eslint-disable-next-line lingui/no-unlocalized-strings
  return `column-form-field-${formFieldId}`;
}

export function reassignColumnIdByIndex(
  columnDefs: Array<ColDef<FormBuilderTableRowDatum>>,
): Array<ColDef<FormBuilderTableRowDatum>> {
  return columnDefs.map((columnDef, index) => {
    const typedContext = columnDef.context as ColumnDefContext;
    if (typedContext?.type === "formField") {
      return {
        ...columnDef,
        colId: getColumnIdFromFormFieldId(typedContext.formField.id),
        field: getColumnIdFromFormFieldId(typedContext.formField.id),
      };
    }
    return { ...columnDef, colId: getColumnIdFromIndex(index), field: getColumnIdFromIndex(index) };
  });
}

export function canDropFieldOfType(draggedItem: DragItemPayload) {
  const extractSpectrumFieldVersion = (draggedItemInternal: DragItemPayload) => {
    if (draggedItemInternal.type === "formField") {
      return draggedItemInternal.formField.spectrumFieldVersion;
    }
    if (draggedItemInternal.type === "spectrumField") {
      return draggedItemInternal.spectrumField;
    }
    return undefined;
  };

  const spectrumFieldVersion = extractSpectrumFieldVersion(draggedItem);
  if (spectrumFieldVersion == null) {
    return true;
  }

  return canSelectColumnOfType(spectrumFieldVersion);
}

export function canSelectColumnOfType(spectrumFieldVersion: SpectrumFieldVersionFields) {
  const unselectableFieldTypePlugins = [
    new SpectrumDocumentFieldPluginDecorator(DocumentFieldPlugin),
    new SpectrumSignatureFieldPluginDecorator(SignatureFieldPlugin),
    new SpectrumObjectFieldPluginDecorator(RegrelloObjectFieldPlugin),
  ];
  return unselectableFieldTypePlugins.every((plugin) => {
    return !plugin.canProcessSpectrumField.bind(plugin)(spectrumFieldVersion);
  });
}

export function getRowDataFromTableData(data: NonNullable<LoadTableQuery["loadTable"]>) {
  const rowData = new Map<number, RowDatum>();

  for (const fieldInstance of data.tableValues) {
    // Validate that the necessary metadata is present.
    if (fieldInstance.spectrumFieldVersion == null) {
      throw new Error(`Spectrum field version is null for field instance value: ${fieldInstance.values[0].id}`);
    }

    const plugin = SpectrumFieldPluginRegistrar.getPluginForSpectrumField(fieldInstance.spectrumFieldVersion);
    const formFieldId = getFieldInstanceFormFieldIdOrThrow(fieldInstance);
    const columnId = getColumnIdFromFormFieldId(formFieldId);

    const createCell = (rowNumber: number, value: unknown) => ({
      id: rowNumber.toString(),
      [columnId]: value,
    });

    const tabularValues = plugin.getTabularValueForFrontend(fieldInstance);
    if (tabularValues.length === 0) {
      // Append a single empty cell.
      rowData.set(0, createCell(0, plugin.getEmptyValueForFrontend()));
    } else {
      // Fill in cells throughout the grid.
      for (const value of tabularValues) {
        const rowDatum = rowData.get(value.rowNumber);
        if (rowDatum == null) {
          rowData.set(value.rowNumber, createCell(value.rowNumber, value.value));
        } else {
          rowDatum[columnId] = value.value;
        }
      }
    }
  }

  return Array.from(rowData.values());
}

/**
 * Construct the table data in a columnar format.
 *
 * @returns Arrays of values on each row for each column keyed by column ID.
 */
export function getColumnarDataFromTableSection(
  table: FormSectionBaseFields,
  spectrumFormInstance: FrontendSpectrumFormInstance,
) {
  const columnarData: Record<string, Array<{ rowNumber: number; value: unknown }>> = {};
  for (const formField of table.formFields) {
    const { fieldInstance } = spectrumFormInstance.getCompositeFieldInstanceByFormField(formField);
    const tabularValues = SpectrumFieldPluginRegistrar.getPluginForSpectrumField(
      formField.spectrumFieldVersion,
    ).getTabularValueForFrontend(fieldInstance);
    columnarData[getColumnIdFromFormFieldId(formField.id)] = tabularValues;
  }
  return columnarData;
}

/**
 * Retrieves all column IDs from an AG Grid instance.
 *
 * @param api - The AG Grid API
 * @returns An array of column IDs. Returns an empty array if no columns exist.
 */
export function dataGridGetAllColumnIds(api: GridApi) {
  const columns = api.getColumns();
  const columnIds = columns?.map((column) => column.getId()) ?? EMPTY_ARRAY;
  return columnIds;
}

/**
 * Retrieves all row nodes from an AG Grid instance.
 *
 * @param api - The AG Grid API
 * @returns An array of row nodes containing the grid data
 */
export function dataGridGetAllRows<TFieldValues extends FieldValues>(api: GridApi) {
  const rowNodes: Array<IRowNode<TFieldValues>> = [];
  api.forEachNode((node) => {
    rowNodes.push(node);
  });
  return rowNodes;
}

/**
 * Retrieves all column definitions from an AG Grid instance.
 *
 * @param api - The AG Grid API
 * @returns An array of column definitions. Returns an empty array if no columns exist.
 */
export function dataGridGetAllColumnDefs(api: GridApi) {
  const columns = api.getColumns();
  const columnDefs = columns?.map((column) => column.getColDef()) ?? EMPTY_ARRAY;
  return columnDefs;
}

/**
 * Finds the next column definition that has a context.type of "formField"
 *
 * @param columnDefs - Array of column definitions
 * @param currentIndex - The current index to search from
 * @returns The next form field column definition or undefined if none found
 */
export function findNextFormFieldColumn(
  columnDefs: Array<ColDef<FormBuilderTableRowDatum>>,
  currentIndex: number,
): ColDef<FormBuilderTableRowDatum> | undefined {
  for (let i = currentIndex + 1; i < columnDefs.length; i += 1) {
    const typedContext = columnDefs[i].context as ColumnDefContext;
    if (typedContext?.type === "formField") {
      return columnDefs[i];
    }
  }
  return undefined;
}

/**
 * Finds the previous column definition that has a context.type of "formField"
 *
 * @param columnDefs - Array of column definitions
 * @param currentIndex - The current index to search from
 * @returns The previous form field column definition or undefined if none found
 */
export function findPreviousFormFieldColumn(
  columnDefs: Array<ColDef<FormBuilderTableRowDatum>>,
  currentIndex: number,
): ColDef<FormBuilderTableRowDatum> | undefined {
  for (let i = currentIndex - 1; i >= 0; i -= 1) {
    const typedContext = columnDefs[i].context as ColumnDefContext;
    if (typedContext?.type === "formField") {
      return columnDefs[i];
    }
  }
  return undefined;
}
